import { useCallback, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { undoAccrualReview } from "@api/accrual-review.api";
import {
    applyApproveToAccrualReviewCache,
    applyUndoToAccrualReviewCache,
    DOCUMENT_MATCH_BILL_QUERY_KEY,
    DOCUMENT_MATCH_INVOICE_QUERY_KEY,
    invalidateAccrualMatchCaches,
} from "@components/accounting/accrualReview/accrualReviewCache";
import { TRANSACTION_DETAILS_QUERY_KEY } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { pluralizeWithCount } from "@common/helpers/string";
import type { AllocationSavePlan } from "./allocationSavePlan";
import {
    AllocationApproval,
    useApproveAllocationEntry,
} from "./useApproveAllocationEntry";

export interface SaveEntry extends AllocationApproval {
    transactionId: number;
}

interface Params {
    onAllSaved: () => void;
}

async function settleAllocationBaseline(queryClient: QueryClient) {
    await Promise.all(
        [
            DOCUMENT_MATCH_INVOICE_QUERY_KEY,
            DOCUMENT_MATCH_BILL_QUERY_KEY,
            TRANSACTION_DETAILS_QUERY_KEY,
        ].map((key) =>
            queryClient.refetchQueries(
                { queryKey: [key], type: "active" },
                { cancelRefetch: false },
            ),
        ),
    );
}

export function useSequentialAllocationSave({ onAllSaved }: Params) {
    const { workspaceId } = useScope();
    const { toast, warning } = useToaster();
    const queryClient = useQueryClient();
    const [isSaving, setIsSaving] = useState(false);
    const [hasCommittedChanges, setHasCommittedChanges] = useState(false);
    const isSavingRef = useRef(false);
    const committedBodiesRef = useRef(new Map<number, string>());
    const { approveAllocation, scheduleResolutionModals } =
        useApproveAllocationEntry();

    const save = useCallback(
        async ({
            entries: allEntries,
            transactionIdsToRelease,
        }: AllocationSavePlan<SaveEntry>) => {
            if (!workspaceId || isSavingRef.current) {
                return;
            }
            const entries = allEntries.filter(
                (entry) =>
                    committedBodiesRef.current.get(entry.transactionId) !==
                    JSON.stringify(entry.body),
            );
            if (entries.length === 0) {
                onAllSaved();
                return;
            }
            isSavingRef.current = true;
            setIsSaving(true);
            let approvedCount = 0;
            let doneCount = 0;
            const unresolvedReleasedIds = new Set<number>();
            const saveEntries = async () => {
                try {
                    for (const transactionId of transactionIdsToRelease) {
                        if (
                            entries.some(
                                (entry) =>
                                    entry.transactionId === transactionId,
                            )
                        ) {
                            applyUndoToAccrualReviewCache(
                                await undoAccrualReview({
                                    workspaceId,
                                    transactionId,
                                }),
                            );
                            committedBodiesRef.current.delete(transactionId);
                            unresolvedReleasedIds.add(transactionId);
                            setHasCommittedChanges(true);
                        }
                    }
                    const recordCommit = (entry: SaveEntry) => {
                        committedBodiesRef.current.set(
                            entry.transactionId,
                            JSON.stringify(entry.body),
                        );
                        unresolvedReleasedIds.delete(entry.transactionId);
                    };
                    for (const entry of entries) {
                        const outcome = await approveAllocation({
                            workspaceId,
                            approval: entry,
                            onApproved: (response) => {
                                recordCommit(entry);
                                approvedCount += 1;
                                setHasCommittedChanges(true);
                                applyApproveToAccrualReviewCache(
                                    entry.transactionId,
                                    response,
                                );
                            },
                        });
                        if (outcome === "cancelled") {
                            break;
                        }
                        if (outcome === "skipped") {
                            recordCommit(entry);
                        }
                        doneCount += 1;
                    }
                } catch (saveError) {
                    // request errors are toasted by approveWithScheduleResolutions / axios interceptor
                    if (!isAxiosError(saveError)) {
                        Sentry.captureException(saveError);
                    }
                }
            };
            await saveEntries().finally(async () => {
                invalidateAccrualMatchCaches(queryClient);
                if (doneCount < entries.length) {
                    await settleAllocationBaseline(queryClient);
                }
                isSavingRef.current = false;
                setIsSaving(false);
            });
            if (doneCount === entries.length) {
                if (approvedCount > 0) {
                    toast("Matches saved");
                }
                onAllSaved();
                return;
            }
            if (approvedCount > 0) {
                const savedCount =
                    allEntries.length - entries.length + approvedCount;
                toast(
                    `Saved ${savedCount} of ${pluralizeWithCount("transaction", allEntries.length)}. Review the rest and save again.`,
                );
            }
            if (unresolvedReleasedIds.size > 0) {
                warning(
                    `Unmatched ${pluralizeWithCount("transaction", unresolvedReleasedIds.size)} from this document to free up line amounts. Save again to rematch.`,
                    { preventAutoClose: true },
                );
            }
        },
        [
            workspaceId,
            approveAllocation,
            onAllSaved,
            queryClient,
            toast,
            warning,
        ],
    );

    return { save, isSaving, hasCommittedChanges, scheduleResolutionModals };
}
